import React, { FunctionComponent, useState, useEffect } from 'react';
import * as styles from '../ExportToFile.module.scss';
import { CreateExtractedDataExportParams } from '../ExportToFile';
import { BiasWarning } from './BiasWarning';
import {
  Button,
  Heading,
  Select,
  FormControl,
  FormLabel,
  FormContainer,
  RadioGroup,
  RadioButton,
} from 'components/core';

type ExtractionFormProps = {
  createExport: (type: string, params: CreateExtractedDataExportParams) => void;
  useNewTemplates: boolean;
  isSingleExtractor: boolean;
};

/* The ExportOptionValue must match the enum options in app/concepts/export/individual_reviewer_form_export.rb */
type ExportOptionValue =
  | 'all'
  | 'consensusOnly'
  | 'deConsensusOnly'
  | 'qaConsensusOnly'
  | 'deConsensusIndividualData'
  | 'qaConsensusIndividualData';

type ExportOption = {
  value: ExportOptionValue;
  description: string;
  warn: boolean;
};

const exportOptionDeConsensusOnly: ExportOption = {
  value: 'deConsensusOnly',
  description: 'Data extraction consensus only (default)',
  warn: false,
};

const exportOptionQaConsensusOnly: ExportOption = {
  value: 'qaConsensusOnly',
  description: 'Quality assessment consensus only',
  warn: false,
};

const exportOptionAll: ExportOption = {
  value: 'all',
  description: 'All data',
  warn: true,
};

const exportOptions: ExportOption[] = [
  exportOptionDeConsensusOnly,
  exportOptionQaConsensusOnly,
  {
    value: 'deConsensusIndividualData',
    description: 'Data extraction consensus + individual data',
    warn: true,
  },
  {
    value: 'qaConsensusIndividualData',
    description: 'Quality assessment consensus + individual data',
    warn: true,
  },
  {
    value: 'consensusOnly',
    description: 'Data extraction & Quality assessment consensus only ',
    warn: false,
  },
  exportOptionAll,
];

const getExportOption = (value: ExportOption['value']) => {
  const matchingOption = exportOptions.find((option) => option.value === value);
  return matchingOption ?? exportOptionAll;
};

const De1ExtractionForm: FunctionComponent<ExtractionFormProps> = (props) => {
  const { createExport, useNewTemplates, isSingleExtractor } = props;
  const [deFormat, setDeFormat] = useState<string>(
    useNewTemplates ? 'section_excel' : 'csv'
  );
  const [qaFormat, setQaFormat] = useState<string>('csv_study_per_row');
  const [exportType, setExportType] = useState<string>('data_extraction');
  const MORE_INFO_TARGET =
    'https://support.covidence.org/help/exporting-your-extracted-data';

  const [
    selectedExportOption,
    setSelectedExportOption,
  ] = useState<ExportOption>(
    isSingleExtractor ? exportOptionAll : exportOptionDeConsensusOnly
  );

  useEffect(() => {
    // If DE1 and user selects XLSX format, data field must change to Consensus Only
    if (deFormat === 'xlsx') {
      setSelectedExportOption(exportOptionDeConsensusOnly);
    }
  }, [deFormat]);

  // Set the csv export type with a use state hook
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    gtag('event', 'Export Extraction Data', {
      event_category: 'engagement',
      event_label: exportType === 'quality_assessment' ? qaFormat : deFormat,
    });
    createExport(exportType, {
      format: exportType === 'quality_assessment' ? qaFormat : deFormat,
      content_filter: selectedExportOption.value,
    });
  };

  const pickExportType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExportType(e.target.value);
    if (e.target.value === 'data_extraction') {
      if (useNewTemplates) {
        setDeFormat('section_excel');
      } else {
        // The new all-data DE export and section Excel export are not supported
        // for legacy templates reviews
        setSelectedExportOption(exportOptionDeConsensusOnly);
      }
    }
    if (e.target.value === 'quality_assessment') {
      setDeFormat('section_excel');
      setQaFormat('csv_study_per_row');
      if (isSingleExtractor) {
        setSelectedExportOption(exportOptionAll);
      } else {
        setSelectedExportOption(exportOptionQaConsensusOnly);
      }
    }
  };

  const pickQaFormat = (e: React.FocusEvent<HTMLSelectElement>) => {
    setQaFormat(e.target.value);
  };

  const pickDeFormat = (e: React.FocusEvent<HTMLSelectElement>) => {
    setDeFormat(e.target.value);
  };

  const pickExportOption = (e: React.FocusEvent<HTMLSelectElement>) => {
    setSelectedExportOption(
      getExportOption(e.target.value as ExportOptionValue)
    );

    // Reset QA format to default when switching data options
    if (exportType === 'quality_assessment') {
      setQaFormat('csv_study_per_row');
    }
  };

  const de1ExtractionContent = () => {
    return (
      <FormContainer className={styles.optionsContainer}>
        <FormControl>
          <FormLabel id="extractionType" size="sm">
            Type
          </FormLabel>
          <RadioGroup
            name="extracted_data_type"
            defaultValue="data_extraction"
            ariaLabelledBy="extractionType"
            onChange={pickExportType}
          >
            <RadioButton value="data_extraction">Data extraction</RadioButton>
            <RadioButton value="quality_assessment">
              Quality assessment
            </RadioButton>
          </RadioGroup>
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="extracted_export_data" size="sm">
            Data
          </FormLabel>
          <Select
            value={selectedExportOption.value}
            className="form-control select required span4"
            id="extracted_export_data"
            onBlur={pickExportOption}
            onChange={pickExportOption}
            disabled={
              deFormat === 'xlsx' ||
              (exportType == 'data_extraction' && !useNewTemplates)
            }
            aria-describedby="biasWarning"
          >
            {!isSingleExtractor && (
              <option
                value={
                  exportType == 'data_extraction'
                    ? 'deConsensusOnly'
                    : 'qaConsensusOnly'
                }
              >
                Consensus only
              </option>
            )}
            {deFormat !== 'xlsx' && (
              <option value="all">All extracted data</option>
            )}
          </Select>
        </FormControl>
        {selectedExportOption.warn && (
          <div role="alert" id="biasWarning">
            <BiasWarning moreInfoTarget={MORE_INFO_TARGET} />
          </div>
        )}
        <FormControl className={styles.exportFormatField}>
          <FormLabel htmlFor="extracted_export_format" size="sm">
            Format
          </FormLabel>
          <Select
            value={exportType === 'quality_assessment' ? qaFormat : deFormat}
            className="form-control select required span4"
            id="extracted_export_format"
            onBlur={
              exportType === 'quality_assessment' ? pickQaFormat : pickDeFormat
            }
            onChange={
              exportType === 'quality_assessment' ? pickQaFormat : pickDeFormat
            }
            disabled={
              exportType === 'quality_assessment' &&
              selectedExportOption.value !== 'qaConsensusOnly'
            }
            aria-describedby="biasWarning"
          >
            {exportType === 'quality_assessment' ? (
              <>
                <option value="csv_study_per_row">
                  One row per study (CSV)
                </option>
                {selectedExportOption.value === 'qaConsensusOnly' && (
                  <option value="csv_domain_per_row">
                    One row per domain (CSV)
                  </option>
                )}
              </>
            ) : (
              <>
                {useNewTemplates && (
                  <option value="section_excel">
                    Sheet per section (Excel)
                  </option>
                )}
                <option value="csv">Single sheet (CSV)</option>
                {!isSingleExtractor && (
                  <option value="xlsx">Sheet per study (Excel)</option>
                )}
              </>
            )}
          </Select>
        </FormControl>
      </FormContainer>
    );
  };

  return (
    <form
      id="export-extracted-data"
      onSubmit={handleSubmit}
      className={styles.exportForm}
    >
      <div>
        <div className={styles.exportFormHeader}>
          <Heading type="h5">Extraction</Heading>
        </div>
        {de1ExtractionContent()}
      </div>
      <div className={styles.exportFormFooter}>
        <Button className={styles.exportFormButton} type="brand">
          Prepare file
        </Button>
      </div>
    </form>
  );
};

export default De1ExtractionForm;
